import React, { useEffect, useMemo, useState } from 'react';
import { TranslationKey, useI18n } from '../../../contexts/I18nContext';
import {
  TELEMETRY_EVENTS, TelemetryEvent, TelemetryItem,
  TelemetryReportItem, TelemetrySession, TelemetrySessionItem,
} from '../../../services/telemetryService';
import { DeviceDetailCore } from '../useDeviceDetail';
import { useDevicesDetailsMonitoring } from './useDevicesDetailsMonitoring';
import { BarChart, CategoryBarChart, LineChart, countsPerHour, minuteOfDay, seriesOf } from './MonitoringCharts';
import { CallSessions } from './CallSessions';
import './DevicesDetailsMonitoring.css';

interface Props {
  core: DeviceDetailCore;
  /** The panel only loads once it is shown. */
  active: boolean;
}

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string;

interface Column<T> {
  key: string;
  labelKey: TranslationKey;
  render: (item: T, t: Translate) => string;
  numeric?: boolean;
  mono?: boolean;
}

const volts = (value: unknown): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(2)} V` : '–';

const text = (value: unknown): string =>
  value === null || value === undefined || value === '' ? '–' : String(value);

/** The telephony event codes, worded. Unknown codes show as they are. */
const CALL_EVENT_KEY: Record<string, TranslationKey> = {
  slic_ring: 'devices.monitoring.calls.slic_ring',
  slic_call: 'devices.monitoring.calls.slic_call',
  slic_connected: 'devices.monitoring.calls.slic_connected',
  slic_dialed: 'devices.monitoring.calls.slic_dialed',
  net_call: 'devices.monitoring.calls.net_call',
  line_connected: 'devices.monitoring.calls.line_connected',
  line_dialed: 'devices.monitoring.calls.line_dialed',
  line_alerted: 'devices.monitoring.calls.line_alerted',
  hang_up: 'devices.monitoring.calls.hang_up',
  dtmf: 'devices.monitoring.eventDtmf',
};

export const callEventLabel = (code: string | null | undefined, t: Translate): string =>
  code ? (CALL_EVENT_KEY[code] ? t(CALL_EVENT_KEY[code]) : code) : '–';

/** The "from" column: who the event concerns (the line or the network), or which way a DTMF went. */
const sideLabel = (item: TelemetrySessionItem, t: Translate): string => {
  if (item.evt === 'dtmf') {
    return item.direction === 'I' ? t('devices.monitoring.dtmfReceived') : item.direction === 'O' ? t('devices.monitoring.dtmfSent') : '–';
  }
  return item.origin === '1' ? t('devices.monitoring.originLine') : item.origin === '2' ? t('devices.monitoring.originNetwork') : text(item.origin);
};

const REPORT_COLUMNS: Column<TelemetryReportItem>[] = [
  { key: 'pwr_lvl', labelKey: 'devices.monitoring.columns.power', render: (i) => volts(i.pwr_lvl), numeric: true },
  { key: 'battery', labelKey: 'devices.monitoring.columns.battery', render: (i) => volts(i.battery), numeric: true },
  { key: 'radio_signal', labelKey: 'devices.monitoring.columns.signal', render: (i) => text(i.radio_signal), numeric: true },
  { key: 'volte', labelKey: 'devices.monitoring.columns.volte', render: (i) => text(i.volte) },
  { key: 'roaming', labelKey: 'devices.monitoring.columns.roaming', render: (i) => text(i.roaming) },
  { key: 'cell_id', labelKey: 'devices.monitoring.columns.cell', render: (i) => text(i.cell_id), mono: true },
  { key: 'mccmnc', labelKey: 'devices.monitoring.columns.network', render: (i) => text(i.mccmnc), mono: true },
  { key: 'next_msg', labelKey: 'devices.monitoring.columns.next', render: (i) => text(i.next_msg) },
];

const SESSION_COLUMNS: Column<TelemetrySessionItem>[] = [
  { key: 'evt', labelKey: 'devices.monitoring.columns.event', render: (i, t) => callEventLabel(i.evt, t) },
  { key: 'side', labelKey: 'devices.monitoring.columns.side', render: (i, t) => sideLabel(i, t) },
  { key: 'key', labelKey: 'devices.monitoring.columns.key', render: (i) => text(i.key), mono: true },
  { key: 'number', labelKey: 'devices.monitoring.columns.number', render: (i) => text(i.number), mono: true },
  { key: 'digit', labelKey: 'devices.monitoring.columns.raw', render: (i) => text(i.digit), mono: true },
  { key: 'evt_id', labelKey: 'devices.monitoring.columns.eventId', render: (i) => text(i.evt_id), mono: true },
];

const EVENT_LABEL: Record<TelemetryEvent, TranslationKey> = {
  report: 'devices.monitoring.eventReport',
  dtmf: 'devices.monitoring.eventDtmf',
  calls: 'devices.monitoring.eventCalls',
  sessions: 'devices.monitoring.eventSessions',
};

/** How many rows the table shows before it scrolls. */
export const VISIBLE_ROWS = 10;

const csq = (value: string | null): number | null => {
  const n = Number(value);
  return value !== null && value !== '' && Number.isFinite(n) ? n : null;
};

const hhmm = (iso: string): string => {
  const m = minuteOfDay(iso);
  return m === null ? '–' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.floor(m % 60)).padStart(2, '0')}`;
};

/**
 * One day of the device's telemetry: a day picker, the kind of event
 * (reports, or calls with their DTMF), a summary strip, charts of the day,
 * the calls one by one with the DTMF sequence each exchanged, and the raw
 * events newest first in a table that shows ten rows and scrolls.
 */
export const DevicesDetailsMonitoring: React.FC<Props> = ({ core, active }) => {
  const { t, formatTime } = useI18n();
  const m = useDevicesDetailsMonitoring(core, active);
  const clock = (iso: string | null) =>
    iso ? formatTime(iso, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '–';
  const items = useMemo<TelemetryItem[]>(() => m.data?.items ?? [], [m.data]);
  const oldestFirst = useMemo(() => [...items].reverse(), [items]);
  // The calls of the day, newest first as served; the charts read them oldest first.
  const sessions = useMemo<TelemetrySession[]>(() => (m.evt === 'sessions' ? m.data?.sessions ?? [] : []), [m.data, m.evt]);
  const sessionsOldestFirst = useMemo(() => [...sessions].reverse(), [sessions]);
  // The selected call (by its start): the latest one with DTMF until the user picks another.
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  useEffect(() => {
    setSelectedStart((sessions.find((s) => s.dtmf_count > 0) ?? sessions[0])?.start ?? null);
  }, [sessions]);
  const selectedIndex = sessionsOldestFirst.findIndex((s) => s.start === selectedStart);

  // Charts of the day, per kind of event.
  const charts = useMemo(() => {
    if (items.length === 0) return null;
    if (m.evt === 'report') {
      const reports = oldestFirst as TelemetryReportItem[];
      return (
        <>
          <LineChart
            title={t('devices.monitoring.chartVoltage')}
            unit="V"
            format={(v) => v.toFixed(1)}
            zeroBased
            emptyLabel={t('devices.monitoring.chartEmpty')}
            series={[
              { id: 'power', label: t('devices.monitoring.columns.power'), points: seriesOf(reports, (i) => i.at, (i) => i.pwr_lvl) },
              { id: 'battery', label: t('devices.monitoring.columns.battery'), points: seriesOf(reports, (i) => i.at, (i) => i.battery) },
            ]}
          />
          <LineChart
            title={t('devices.monitoring.chartSignal')}
            unit="CSQ"
            format={(v) => String(Math.round(v))}
            zeroBased
            emptyLabel={t('devices.monitoring.chartEmpty')}
            series={[{ id: 'csq', label: t('devices.monitoring.columns.signal'), points: seriesOf(reports, (i) => i.at, (i) => csq(i.radio_signal)) }]}
          />
        </>
      );
    }
    // Calls: the DTMF exchanged in each call (the unit that makes a sequence
    // readable: a transmission to the receiver is a hundred digits in one
    // call), and the calls over the hours of the day.
    return (
      <>
        <CategoryBarChart
          title={t('devices.monitoring.chartDtmfPerCall')}
          seriesLabels={[t('devices.monitoring.dtmfReceived'), t('devices.monitoring.dtmfSent')]}
          emptyLabel={t('devices.monitoring.chartNoDtmf')}
          selected={selectedIndex < 0 ? null : selectedIndex}
          onSelect={(i) => setSelectedStart(sessionsOldestFirst[i]?.start ?? null)}
          categories={sessionsOldestFirst.map((s) => {
            const received = s.dtmf.filter((d) => d.direction === 'I').length;
            return { label: hhmm(s.start), tooltipTitle: `${hhmm(s.start)} · ${s.number ?? ''}`.trim(), values: [received, s.dtmf.length - received] };
          })}
        />
        <BarChart
          title={t('devices.monitoring.chartCalls')}
          perHour={countsPerHour(sessions.map((s) => s.start))}
          valueLabel={t('devices.monitoring.eventCalls')}
          emptyLabel={t('devices.monitoring.chartEmpty')}
        />
      </>
    );
  }, [items, oldestFirst, sessions, sessionsOldestFirst, selectedIndex, m.evt, t]);

  // Extra figures of the summary strip, per kind of event.
  const extras = useMemo(() => {
    if (!m.summary || items.length === 0) return [];
    if (m.evt === 'report') {
      const newest = items[0] as TelemetryReportItem;
      return [
        { label: t('devices.monitoring.summaryBattery'), value: volts(newest.battery) },
        { label: t('devices.monitoring.summaryPower'), value: volts(newest.pwr_lvl) },
        { label: t('devices.monitoring.summarySignal'), value: text(newest.radio_signal) },
      ];
    }
    const dtmf = sessions.flatMap((s) => s.dtmf);
    const received = dtmf.filter((d) => d.direction === 'I').length;
    return [
      { label: t('devices.monitoring.summaryCalls'), value: String(sessions.filter((s) => s.opened_by !== 'dtmf').length) },
      { label: t('devices.monitoring.summaryCallsWithDtmf'), value: String(sessions.filter((s) => s.dtmf_count > 0).length) },
      { label: t('devices.monitoring.dtmfReceived'), value: String(received) },
      { label: t('devices.monitoring.dtmfSent'), value: String(dtmf.length - received) },
    ];
  }, [items, sessions, m.evt, m.summary, t]);

  const cell = (col: { numeric?: boolean; mono?: boolean }) =>
    [col.numeric ? 'numeric' : '', col.mono ? 'mono' : ''].filter(Boolean).join(' ') || undefined;

  const renderRows = () => {
    if (m.evt === 'report') {
      return (items as TelemetryReportItem[]).map((item, index) => (
        <tr key={`${item.at ?? ''}-${index}`}>
          <td className="monitoring-time">{clock(item.at)}</td>
          {REPORT_COLUMNS.map((col) => <td key={col.key} className={cell(col)}>{col.render(item, t)}</td>)}
        </tr>
      ));
    }
    return (items as TelemetrySessionItem[]).map((item, index) => (
      <tr key={`${item.at ?? ''}-${index}`} className={item.evt === 'dtmf' ? 'is-dtmf' : undefined}>
        <td className="monitoring-time">{clock(item.at)}</td>
        {SESSION_COLUMNS.map((col) => <td key={col.key} className={cell(col)}>{col.render(item, t)}</td>)}
      </tr>
    ));
  };
  const columns: Array<{ key: string; labelKey: TranslationKey; numeric?: boolean }> =
    m.evt === 'report' ? REPORT_COLUMNS : SESSION_COLUMNS;

  return (
    <div className="modal-content tab-content monitoring-panel">
      <div className="monitoring-toolbar">
        <div className="monitoring-day" role="group" aria-label={t('devices.monitoring.dayLabel')}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={m.goPreviousDay} disabled={m.loading} title={t('devices.monitoring.previousDay')} aria-label={t('devices.monitoring.previousDay')}>
            ‹
          </button>
          <input
            type="date"
            className="monitoring-date-input"
            value={m.date}
            max={m.today}
            onChange={(e) => m.pickDate(e.target.value)}
            disabled={m.loading}
            aria-label={t('devices.monitoring.dayLabel')}
            data-testid="monitoring-date"
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={m.goNextDay} disabled={m.loading || m.isToday} title={t('devices.monitoring.nextDay')} aria-label={t('devices.monitoring.nextDay')}>
            ›
          </button>
          {!m.isToday && (
            <button type="button" className="btn btn-link btn-sm" onClick={m.goToday} disabled={m.loading}>
              {t('devices.monitoring.today')}
            </button>
          )}
        </div>

        <div className="monitoring-kinds" role="tablist" aria-label={t('devices.monitoring.eventLabel')}>
          {TELEMETRY_EVENTS.map((code) => (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={m.evt === code}
              className={`monitoring-kind${m.evt === code ? ' is-active' : ''}`}
              onClick={() => m.setEvt(code)}
              disabled={m.loading}
              data-testid={`monitoring-kind-${code}`}
            >
              {t(EVENT_LABEL[code])}
            </button>
          ))}
        </div>

        <button type="button" className="btn btn-secondary btn-sm monitoring-refresh" onClick={m.refresh} disabled={m.loading} data-testid="monitoring-refresh">
          {m.loading ? t('devices.monitoring.loading') : t('devices.monitoring.refresh')}
        </button>
      </div>

      {m.failure === 'unavailable' && (
        <p className="monitoring-notice monitoring-notice-muted">{t('devices.monitoring.unavailable')}</p>
      )}
      {m.failure === 'forbidden' && (
        <p className="monitoring-notice monitoring-notice-error">{t('devices.monitoring.forbidden')}</p>
      )}
      {m.failure === 'failed' && (
        <p className="monitoring-notice monitoring-notice-error">{t('devices.monitoring.failed')}</p>
      )}

      {m.summary && (
        <div className="monitoring-summary" data-testid="monitoring-summary">
          <div className="monitoring-stat">
            <span className="monitoring-stat-value">{m.summary.count}</span>
            <span className="monitoring-stat-label">{t('devices.monitoring.summaryCount')}</span>
          </div>
          <div className="monitoring-stat">
            <span className="monitoring-stat-value">{clock(m.summary.firstAt)}</span>
            <span className="monitoring-stat-label">{t('devices.monitoring.summaryFirst')}</span>
          </div>
          <div className="monitoring-stat">
            <span className="monitoring-stat-value">{clock(m.summary.lastAt)}</span>
            <span className="monitoring-stat-label">{t('devices.monitoring.summaryLast')}</span>
          </div>
          {extras.map((x) => (
            <div className="monitoring-stat" key={x.label}>
              <span className="monitoring-stat-value">{x.value}</span>
              <span className="monitoring-stat-label">{x.label}</span>
            </div>
          ))}
        </div>
      )}

      {m.summary?.truncated && (
        <p className="monitoring-notice monitoring-notice-warning">
          {t('devices.monitoring.truncated', { shown: m.summary.count, total: m.summary.total })}
        </p>
      )}

      {m.data && items.length === 0 && !m.failure && (
        <p className="monitoring-empty">{t('devices.monitoring.empty')}</p>
      )}

      {charts && <div className="monitoring-charts" data-testid="monitoring-charts">{charts}</div>}

      {m.evt === 'sessions' && sessions.length > 0 && (
        <>
          <div className="monitoring-table-head">
            <span>{t('devices.monitoring.sessions.title', { count: sessions.length })}</span>
            <span className="monitoring-table-hint">{t('devices.monitoring.sessions.hint')}</span>
          </div>
          <CallSessions sessions={sessions} selectedStart={selectedStart} onSelect={setSelectedStart} />
        </>
      )}

      {items.length > 0 && m.evt !== 'sessions' && (
        <>
          <div className="monitoring-table-head">
            <span>{t('devices.monitoring.tableTitle', { count: items.length })}</span>
            {items.length > VISIBLE_ROWS && <span className="monitoring-table-hint">{t('devices.monitoring.tableScroll', { visible: VISIBLE_ROWS })}</span>}
          </div>
          <div className="monitoring-table-wrap" style={{ '--visible-rows': VISIBLE_ROWS } as React.CSSProperties}>
            <table className="monitoring-table" data-testid="monitoring-table">
              <thead>
                <tr>
                  <th>{t('devices.monitoring.columns.time')}</th>
                  {columns.map((col) => (
                    <th key={col.key} className={col.numeric ? 'numeric' : undefined}>{t(col.labelKey)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
